// The whole EFP flow behind one hook: read the file, resolve it, plan it, post it.
//
// Both entry points use this, so the Upload Contract modal and the chat cannot end up
// applying different rules to the same contract.
import mammoth from 'mammoth';
import { useCallback, useMemo, useState } from 'react';
import {
    AllocationModel,
    GetCustomerDataResponse,
    PositionModel,
} from '../../../../Generated/Commodity-Management-Api';
import { EfpContractFields, parseEfpContract } from './EfpContractParser';
import {
    EfpEntityOverrides,
    EfpResolution,
    canPlanEfpContract,
    resolveEfpContract,
    toEfpPlanInput,
} from './EfpContractResolver';
import { EfpBasisSource, EfpLeg, EfpPlan, buildEfpPlan } from './EfpPositionPlan';
import { EfpSubmitResult, submitEfpPlan } from './EfpSubmitter';
import { EfpImpliedBasis, findBushelWeight, impliedBasis } from './EfpUnits';

export interface UseEfpContractOptions {
    customerData: GetCustomerDataResponse;
    allocations: readonly AllocationModel[];
    positions: readonly PositionModel[];
}

/** The header fields the review screen lets the user correct. */
export type EfpEditableField = keyof Pick<EfpContractFields,
    'contractNumber' | 'contractDate' | 'sellerName' | 'plantName' | 'commodityName'
    | 'futuresMonth' | 'deliveryMonth' | 'volume' | 'unit'
    | 'boardPrice' | 'boardPriceUnit' | 'basisPrice' | 'basisPriceUnit' | 'currency'>;

export type EfpEntityKind = 'commodity' | 'counterparty' | 'location';

export interface UseEfpContract {
    file: File | null;
    /**
     * What the confirmation said, with the user's corrections applied over it, and the
     * basis filled in from the net price when the confirmation left it blank.
     */
    fields: EfpContractFields | null;
    /** What the confirmation said before any correction. */
    parsedFields: EfpContractFields | null;
    /** Set when the basis in `fields` was worked out from the net price rather than stated or typed. */
    impliedBasis: EfpImpliedBasis | null;
    resolution: EfpResolution | null;
    plan: EfpPlan | null;
    /** Nothing blocking - safe to post. */
    canSubmit: boolean;
    isParsing: boolean;
    isSubmitting: boolean;
    parseError: string | null;
    loadFile: (file: File) => Promise<void>;
    submit: () => Promise<EfpSubmitResult>;
    reset: () => void;
    changeLeg: (index: number, changes: Partial<Pick<EfpLeg, 'volume' | 'price'>>) => void;
    /** Correct a header field the confirmation got wrong or left blank. */
    changeField: <K extends EfpEditableField>(key: K, value: EfpContractFields[K] | undefined) => void;
    /** Pick the site record a name should have resolved to. `null` clears the pick. */
    chooseEntity: (kind: EfpEntityKind, id: number | null) => void;
    /** When the contract's basis and the existing position's differ, say which to book. */
    chooseBasisSource: (source: EfpBasisSource) => void;
}

export function isEfpContractFile(file: File): boolean {
    return file.name.toLowerCase().endsWith('.docx');
}

export function useEfpContract(options: UseEfpContractOptions): UseEfpContract {
    const { customerData, allocations, positions } = options;

    const [file, setFile] = useState<File | null>(null);
    const [parsedFields, setParsedFields] = useState<EfpContractFields | null>(null);
    const [isParsing, setIsParsing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [parseError, setParseError] = useState<string | null>(null);
    // Header corrections, laid over what was parsed. A key set to undefined is a field
    // the user cleared, so the spread below has to keep it.
    const [fieldEdits, setFieldEdits] = useState<Partial<EfpContractFields>>({});
    // Records the user picked where the name did not resolve (or resolved wrongly).
    const [entityOverrides, setEntityOverrides] = useState<EfpEntityOverrides>({});
    // Corrections the user made in the grid, by leg index.
    const [legEdits, setLegEdits] = useState<Record<number, Partial<Pick<EfpLeg, 'volume' | 'price'>>>>({});
    // Which basis to book when the contract and the book disagree.
    const [basisSource, setBasisSource] = useState<EfpBasisSource | undefined>(undefined);

    const editedFields = useMemo<EfpContractFields | null>(
        () => (parsedFields ? { ...parsedFields, ...fieldEdits } : null),
        [parsedFields, fieldEdits]
    );

    // Resolved before the basis is implied: the commodity is needed to find the bushel
    // weight, and nothing about resolving depends on the basis.
    const preliminaryResolution = useMemo(
        () => (editedFields ? resolveEfpContract(editedFields, customerData, allocations, entityOverrides) : null),
        [editedFields, customerData, allocations, entityOverrides]
    );

    // A confirmation that gives a net price and a board price but no basis has still told
    // us the basis. It is filled in here, not in the parser, because restating the board
    // per the net's unit needs the bushel weight, which only the book knows. Typing a
    // basis takes precedence; clearing the field brings the implied one back.
    const implied = useMemo<EfpImpliedBasis | null>(() => {
        if (!editedFields || editedFields.basisPrice !== undefined) return null;
        const bushelWeight = findBushelWeight(positions, preliminaryResolution?.commodity?.commodityId);
        return impliedBasis(editedFields, bushelWeight) ?? null;
    }, [editedFields, positions, preliminaryResolution?.commodity?.commodityId]);

    const fields = useMemo<EfpContractFields | null>(
        () => (editedFields && implied
            ? { ...editedFields, basisPrice: implied.value, basisPriceUnit: implied.unit }
            : editedFields),
        [editedFields, implied]
    );

    const resolution = useMemo(
        () => (implied && fields
            ? resolveEfpContract(fields, customerData, allocations, entityOverrides)
            : preliminaryResolution),
        [implied, fields, customerData, allocations, entityOverrides, preliminaryResolution]
    );

    const plan = useMemo(() => {
        if (!fields || !resolution || !canPlanEfpContract(fields, resolution)) return null;

        const built = buildEfpPlan(toEfpPlanInput(fields, resolution, positions, { basisSource }));
        if (Object.keys(legEdits).length === 0) return built;

        return {
            ...built,
            legs: built.legs.map((leg, index) => ({ ...leg, ...legEdits[index] })),
        };
    }, [fields, resolution, positions, legEdits, basisSource]);

    const changeLeg = useCallback((index: number, changes: Partial<Pick<EfpLeg, 'volume' | 'price'>>) => {
        setLegEdits(previous => ({ ...previous, [index]: { ...previous[index], ...changes } }));
    }, []);

    // A header change rebuilds every leg, so corrections made to the old legs no longer
    // describe anything and are dropped rather than applied to the wrong rows.
    const changeField = useCallback(<K extends EfpEditableField>(key: K, value: EfpContractFields[K] | undefined) => {
        setFieldEdits(previous => ({ ...previous, [key]: value }));
        setLegEdits({});
    }, []);

    const chooseEntity = useCallback((kind: EfpEntityKind, id: number | null) => {
        setEntityOverrides(previous => {
            switch (kind) {
                case 'commodity': return { ...previous, commodityId: id };
                case 'counterparty': return { ...previous, counterpartyId: id };
                case 'location': return { ...previous, locationId: id };
                default: return previous;
            }
        });
        setLegEdits({});
    }, []);

    // The basis leg is repriced by the choice, so a manual price on it would be stale.
    const chooseBasisSource = useCallback((source: EfpBasisSource) => {
        setBasisSource(source);
        setLegEdits({});
    }, []);

    const canSubmit = !!plan
        && plan.errors.length === 0
        && (resolution?.unresolved.length ?? 0) === 0
        && (resolution?.missingFields.length ?? 0) === 0;

    const loadFile = useCallback(async (next: File) => {
        setIsParsing(true);
        setParseError(null);
        try {
            const { value } = await mammoth.extractRawText({ arrayBuffer: await next.arrayBuffer() });
            setParsedFields(parseEfpContract(value));
            setFile(next);
        } catch (error) {
            setParseError((error as Error)?.message ?? 'The contract could not be read');
            setParsedFields(null);
            setFile(null);
        } finally {
            // Corrections belong to the file they were made against.
            setFieldEdits({});
            setEntityOverrides({});
            setLegEdits({});
            setBasisSource(undefined);
            setIsParsing(false);
        }
    }, []);

    const submit = useCallback(async (): Promise<EfpSubmitResult> => {
        if (!plan || !fields || !resolution) {
            return {
                allocationCount: 0, basisReduced: false, documentStored: false,
                errors: ['There is nothing to submit'],
            };
        }

        setIsSubmitting(true);
        try {
            // The parent hedge is held against whichever counterparty the existing futures
            // carry, which is rarely the supplier on the contract.
            const parentFutures = positions.find(position =>
                position.location?.locationId === resolution.parentLocation?.locationId
                && position.commodity?.commodityId === resolution.commodity?.commodityId
                && position.counterparty?.counterpartyId !== undefined);

            return await submitEfpPlan(plan, {
                commodityId: resolution.commodity!.commodityId,
                counterpartyId: resolution.counterparty!.counterpartyId,
                parentCounterpartyId: parentFutures?.counterparty?.counterpartyId,
                allocationGroup: resolution.allocationGroup,
                tradeDate: fields.contractDate ?? new Date().toISOString().slice(0, 10),
                dealTicketNumber: fields.contractNumber,
                contractFile: file ?? undefined,
            });
        } finally {
            setIsSubmitting(false);
        }
    }, [plan, fields, resolution, positions, file]);

    const reset = useCallback(() => {
        setFile(null);
        setParsedFields(null);
        setParseError(null);
        setFieldEdits({});
        setEntityOverrides({});
        setLegEdits({});
        setBasisSource(undefined);
    }, []);

    return {
        file, fields, parsedFields, impliedBasis: implied, resolution, plan, canSubmit,
        isParsing, isSubmitting, parseError,
        loadFile, submit, reset, changeLeg, changeField, chooseEntity, chooseBasisSource,
    };
}
